<template>
  <div class="overview-panel">
    <!-- KPI Banner -->
    <div v-if="kpi" class="kpi-row">
      <div class="kpi-card">
        <div class="kpi-value">{{ kpi.activeProjects }}</div>
        <div class="kpi-label">Projetos ativos</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-value">{{ kpi.totalTasks }}</div>
        <div class="kpi-label">Tarefas total</div>
      </div>
      <div class="kpi-card kpi-card--success">
        <div class="kpi-value">{{ kpi.completedTasks }}</div>
        <div class="kpi-label">Concluídas</div>
      </div>
      <div class="kpi-card kpi-card--warning">
        <div class="kpi-value">{{ kpi.inProgressTasks }}</div>
        <div class="kpi-label">Em andamento</div>
      </div>
      <div class="kpi-card" :class="kpi.overdueTasks > 0 ? 'kpi-card--danger' : ''">
        <div class="kpi-value">{{ kpi.overdueTasks }}</div>
        <div class="kpi-label">Atrasadas</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-value">{{ kpi.teamMembers }}</div>
        <div class="kpi-label">Equipe</div>
      </div>
    </div>
    <div v-else-if="kpiLoading" class="kpi-row">
      <div v-for="i in 6" :key="i" class="kpi-card kpi-card--skeleton">
        <SkeletonBlock width="36px" height="24px" />
        <SkeletonBlock width="70%" height="10px" />
      </div>
    </div>

    <SkeletonList v-if="showProjectsSkeleton" variant="card" :count="3" />
    <div v-else-if="projects.length === 0" class="empty-state">
      <div class="empty-text">Nenhum projeto cadastrado.</div>
    </div>

    <div v-else class="project-grid">
      <article
        v-for="proj in projects"
        :key="proj.id"
        class="project-card clickable-card"
        role="button"
        tabindex="0"
        @click="$emit('extend-project', proj.id)"
        @keydown.enter.prevent="$emit('extend-project', proj.id)"
        @keydown.space.prevent="$emit('extend-project', proj.id)"
      >
        <div class="project-main">
          <div class="project-left">
            <h3 class="project-title">{{ proj.name }}</h3>
            <div class="item-meta">Responsável: {{ leadResponsible(proj) }}</div>
            <div class="item-meta">
              Status:
              <span :class="insightFor(proj.id).status === 'atrasado' ? 'status-late' : 'status-ok'">
                {{ insightFor(proj.id).status === 'atrasado' ? 'Atrasado' : 'Em dia' }}
              </span>
            </div>
            <div class="item-meta">Previsão de conclusão: {{ forecastText(proj.id) }}</div>
            <div class="gauge-wrap">
              <div class="gauge-shell">
                <svg viewBox="0 0 200 120" class="gauge-svg" aria-hidden="true">
                  <path class="gauge-track" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" />
                  <path class="gauge-planned" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" :style="arcStyle(insightFor(proj.id).plannedPercent)" />
                  <path class="gauge-actual" d="M20 100 A80 80 0 0 1 180 100" pathLength="100" :style="arcStyle(insightFor(proj.id).actualPercent)" />
                </svg>
                <div class="gauge-value">{{ percentText(insightFor(proj.id).actualPercent) }}</div>
              </div>
              <div class="gauge-legend">
                <span><i class="dot dot-planned"></i>Planejado {{ percentText(insightFor(proj.id).plannedPercent) }}</span>
                <span><i class="dot dot-actual"></i>Real {{ percentText(insightFor(proj.id).actualPercent) }}</span>
              </div>
            </div>
          </div>
          <div class="project-right">
            <img v-if="hasPhoto(proj.photoDataUrl)" class="project-photo" :src="photoSrc(proj.photoDataUrl)" :alt="proj.name" />
            <div v-else class="project-photo project-photo-empty">Sem foto</div>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, type PropType } from 'vue'
import { projectsStore, type Project } from '@/store/projects'
import type { Account } from '@/store/accounts'
import { apiFetch } from '@/store/api'
import SkeletonBlock from '@/components/skeleton/SkeletonBlock.vue'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

interface KpiData {
  totalTasks: number
  completedTasks: number
  overdueTasks: number
  pendingTasks: number
  inProgressTasks: number
  activeProjects: number
  teamMembers: number
}

export default defineComponent({
  name: 'OverviewPanel',
  components: { SkeletonBlock, SkeletonList },
  props: {
    projects: { type: Array as PropType<Project[]>, default: () => [] },
    projectInsights: {
      type: Object as PropType<Record<string, { status: 'em-dia' | 'atrasado'; forecastDate: string; plannedPercent: number; actualPercent: number }>>,
      default: () => ({})
    },
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    formatReportDate: { type: Function as PropType<(value: string) => string>, required: true }
  },
  emits: ['extend-project'],
  setup(props) {
    const kpi = ref<KpiData | null>(null)
    const kpiLoading = ref(true)
    const showProjectsSkeleton = computed(() => projectsStore.state.loading && props.projects.length === 0)
    onMounted(async () => {
      try {
        const data = await apiFetch('/kpi-summary')
        if (data.ok) kpi.value = data.kpi
      } catch { /* silent */ } finally {
        kpiLoading.value = false
      }
    })
    const insightFor = (projectId: string) =>
      props.projectInsights[projectId] || { status: 'em-dia', forecastDate: '', plannedPercent: 0, actualPercent: 0 }

    const forecastText = (projectId: string) => {
      const info = insightFor(projectId)
      if (!info.forecastDate) return '-'
      return props.formatReportDate(info.forecastDate)
    }

    const leadResponsible = (proj: Project) => {
      if (proj.responsibleNames && proj.responsibleNames.length > 0) return proj.responsibleNames[0]
      if (proj.ownerName) return proj.ownerName
      return '-'
    }

    const clampPercent = (value: number) => Math.max(0, Math.min(100, Number(value || 0)))
    const percentText = (value: number) => `${Math.round(clampPercent(value))}%`
    const arcStyle = (value: number) => ({ strokeDasharray: `${clampPercent(value)} 100` })

    const hasPhoto = (value: string) => Boolean(`${value || ''}`.trim())
    const photoSrc = (value: string) => {
      const raw = `${value || ''}`.trim()
      if (!raw) return ''
      if (raw.startsWith('/uploads/')) {
        const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
        return `${apiBase}${raw}`
      }
      if (
        raw.startsWith('data:') ||
        raw.startsWith('http://') ||
        raw.startsWith('https://') ||
        raw.startsWith('/')
      ) {
        return raw
      }
      return `data:image/jpeg;base64,${raw}`
    }

    return { kpi, kpiLoading, showProjectsSkeleton, insightFor, forecastText, leadResponsible, percentText, arcStyle, hasPhoto, photoSrc }
  }
})
</script>

<style scoped lang="scss">
.kpi-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.kpi-card {
  flex: 1 1 90px;
  min-width: 80px;
  background: var(--card, #fff);
  border: 1px solid rgba(31, 30, 26, 0.1);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &--success { border-color: #bbf7d0; background: #f0fdf4; }
  &--warning { border-color: #fde68a; background: #fffbeb; }
  &--danger  { border-color: #fecaca; background: #fef2f2; }
  &--skeleton { gap: 8px; }
}

.kpi-value {
  font-size: 26px;
  font-weight: 800;
  color: var(--ink, #1f1e1a);
  line-height: 1;
}

.kpi-label {
  font-size: 11px;
  color: var(--ink-muted, #888);
  font-weight: 500;
  white-space: nowrap;
}

.project-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.project-card {
  border: 1px solid rgba(31, 30, 26, 0.12);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.9);
}

.clickable-card {
  cursor: pointer;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.clickable-card:hover,
.clickable-card:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(31, 30, 26, 0.1);
  border-color: rgba(79, 51, 0, 0.35);
  outline: none;
}

.project-main {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: grid;
  grid-template-columns: 3fr 2fr;
}

.project-left {
  padding: 14px;
  display: grid;
  align-content: start;
  gap: 8px;
}

.project-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.08;
}

.gauge-wrap { margin-top: 8px; }
.gauge-shell { width: 220px; max-width: 100%; position: relative; }
.gauge-svg { width: 100%; height: auto; display: block; aspect-ratio: 220 / 120; }

.gauge-track,
.gauge-planned,
.gauge-actual {
  fill: none;
  stroke-linecap: round;
  stroke-width: 12;
  stroke-dasharray: 0 100;
}
.gauge-track  { stroke: rgba(31, 30, 26, 0.16); stroke-dasharray: 100 100; }
.gauge-planned { stroke: #e1a12f; opacity: .95; }
.gauge-actual  { stroke: var(--system-navbar, #4f3300); }

.gauge-value {
  position: absolute;
  left: 50%;
  bottom: 2px;
  transform: translateX(-50%);
  font-size: 20px;
  font-weight: 800;
  color: #1f1e1a;
}

.gauge-legend { display: grid; gap: 4px; font-size: 12px; color: #4b463f; margin-top: 2px; }
.dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 6px; vertical-align: middle; }
.dot-planned { background: #e1a12f; }
.dot-actual  { background: var(--system-navbar, #4f3300); }

.project-right { min-width: 0; }

.project-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-photo-empty {
  display: grid;
  place-items: center;
  background: #ececec;
  color: #646464;
  font-size: 13px;
}

.status-ok   { color: #1f8a45; font-weight: 700; }
.status-late { color: #bf2b2b; font-weight: 700; }

.empty-state { height: 60vh; display: grid; place-items: center; }
.empty-text  { color: var(--ink-muted); }

@media (min-aspect-ratio: 4/3) and (min-width: 1100px) {
  .project-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .kpi-row { gap: 8px; }
  .kpi-card { padding: 10px 12px; }
  .kpi-value { font-size: 20px; }

  .project-title { font-size: 20px; }

  .project-main {
    grid-template-columns: 1fr;
    aspect-ratio: auto;
  }
  .project-right { max-height: 180px; }
  .project-left  { padding-bottom: 16px; }
}
</style>
